import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '@/api/client';
import type { PoolsResponse, TokensResponse } from '@/api/types';
import { CHAIN_ID, EXPLORER_BASE } from '@/config';

function explorerAddress(addr: string) {
  return `${EXPLORER_BASE}/address/${addr}`;
}

async function fetchPoolsForToken(address: string): Promise<PoolsResponse['pools']> {
  try {
    const res = await fetchApi<PoolsResponse>(
      `/api/v1/tokens/${address}/pools?chainId=${CHAIN_ID}`,
    );
    return res.pools || [];
  } catch {
    return [];
  }
}

export function PoolsPage() {
  const tokensQuery = useQuery({
    queryKey: ['tokens-pools', CHAIN_ID],
    queryFn: () =>
      fetchApi<TokensResponse>(`/api/v1/tokens?chainId=${CHAIN_ID}&limit=150&includeDodoPool=true`),
    staleTime: 30_000,
  });

  const poolAggQuery = useQuery({
    queryKey: ['aggregate-pools', tokensQuery.data?.tokens?.map((t) => t.address).join(',')],
    enabled: Boolean(tokensQuery.data?.tokens?.length),
    queryFn: async () => {
      const tokens = tokensQuery.data!.tokens.filter((t) =>
        (t.symbol || '').toLowerCase().startsWith('c'),
      );
      const slice = tokens.slice(0, 48);
      const rows: Array<{
        token: string;
        symbol?: string;
        pool: PoolsResponse['pools'][0];
      }> = [];
      const batchSize = 8;
      for (let i = 0; i < slice.length; i += batchSize) {
        const batch = slice.slice(i, i + batchSize);
        const results = await Promise.all(
          batch.map(async (t) => {
            const pools = await fetchPoolsForToken(t.address);
            return pools.map((pool) => ({ token: t.address, symbol: t.symbol, pool }));
          }),
        );
        for (const r of results) rows.push(...r);
      }
      return rows;
    },
    staleTime: 45_000,
  });

  return (
    <>
      <h1>Liquidity pools</h1>
      <p className="lead">
        Pools are loaded from the token-aggregation indexer for Chain {CHAIN_ID} compliant (c*)
        tokens (sampled up to 48 symbols). TVL and volume update with the service refresh cycle.
      </p>

      {tokensQuery.isError && (
        <div className="err-box">{(tokensQuery.error as Error).message}</div>
      )}
      {poolAggQuery.isFetching && <p className="muted">Loading pool details…</p>}
      {poolAggQuery.isError && (
        <div className="err-box">{(poolAggQuery.error as Error).message}</div>
      )}

      {poolAggQuery.data && (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Pool</th>
                <th>DEX</th>
                <th>Pair</th>
                <th>TVL</th>
                <th>Vol 24h</th>
              </tr>
            </thead>
            <tbody>
              {poolAggQuery.data.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    No pools returned for sampled tokens (API unreachable or indexer empty).
                  </td>
                </tr>
              )}
              {poolAggQuery.data.map(({ pool, token, symbol }) => (
                <tr key={`${pool.address}-${token}`}>
                  <td className="mono">
                    <a href={explorerAddress(pool.address)} target="_blank" rel="noreferrer">
                      {pool.address.slice(0, 8)}…{pool.address.slice(-6)}
                    </a>
                  </td>
                  <td>{pool.dex || '—'}</td>
                  <td>
                    <span className="badge badge-c">{symbol || '?'}</span>{' '}
                    {pool.token0?.symbol || '?'} / {pool.token1?.symbol || '?'}
                  </td>
                  <td>{pool.tvl ?? '—'}</td>
                  <td>{pool.volume24h ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
